import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getBackendErrorMessage } from '../utils/toastError';
import AppUpdateRequiredModal from '../components/AppUpdateRequiredModal';

const AuthContext = createContext();

let notifyMandatoryUpdate = null;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Configure axios defaults
// HARDCODED para Railway - detectar producción por hostname o Capacitor
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const isNativeCapacitor = typeof window !== 'undefined' &&
  !!window.Capacitor &&
  (window.Capacitor.isNativePlatform?.() === true ||
   window.location.protocol === 'capacitor:' ||
   window.location.origin.startsWith('capacitor://'));

const isProduction = typeof window !== 'undefined' &&
  !isLocalhost &&
  (window.location.hostname === 'mundoxyz.com' ||
    window.location.hostname === 'www.mundoxyz.com' ||
    window.location.hostname.endsWith('.mundoxyz.com') ||
    window.location.hostname.endsWith('.mundoxyz') ||
    window.location.hostname.includes('mundoxyz') ||
    window.location.hostname.includes('railway.app') ||
    isNativeCapacitor);

const apiUrl = isNativeCapacitor
  ? (process.env.REACT_APP_API_URL || 'https://mundoxyz.com')
  : isProduction
    ? (typeof window !== 'undefined' && window.location.origin ? window.location.origin : (process.env.REACT_APP_API_URL || 'https://mundoxyz.com'))
    : (process.env.REACT_APP_API_URL || '');

// Set baseURL if we have a URL (production or explicit env var)
if (apiUrl && apiUrl !== '') {
  const cleanUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  console.log('🔧 axios baseURL configurado:', cleanUrl);
  console.log('🌍 Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'SSR');
  console.log('🏭 isProduction:', isProduction);
  axios.defaults.baseURL = cleanUrl;
} else {
  console.log('⚠️  No API URL set, usando rutas relativas (development)');
}

axios.defaults.withCredentials = true;

const getCachedCapacitorAppVersion = () => {
  try {
    if (typeof window === 'undefined') return null;
    const v = window.localStorage.getItem('mx_app_version');
    return (v || '').toString().trim() || null;
  } catch (_) {
    return null;
  }
};

const cacheCapacitorAppVersion = (v) => {
  try {
    if (typeof window === 'undefined') return;
    const s = (v || '').toString().trim();
    if (!s) return;
    window.localStorage.setItem('mx_app_version', s);
  } catch (_) {
    // ignore
  }
};

const normalizeVersionParts = (v) => {
  const raw = (v || '').toString().trim();
  if (!raw) return null;
  const parts = raw.split('.').map((p) => {
    const n = parseInt(p, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  });
  if (!parts.length || parts.some((p) => p == null)) return null;
  return parts;
};

const compareVersions = (a, b) => {
  const pa = normalizeVersionParts(a);
  const pb = normalizeVersionParts(b);
  if (!pa || !pb) return null;
  const max = Math.max(pa.length, pb.length);
  for (let i = 0; i < max; i += 1) {
    const va = pa[i] || 0;
    const vb = pb[i] || 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
};

const ensureCapacitorAppVersionCached = async () => {
  if (!isNativeCapacitor) return null;
  const cached = getCachedCapacitorAppVersion();
  if (cached) return cached;
  try {
    const cap = await import('@capacitor/app');
    const info = await cap.App.getInfo();
    const v = (info?.version || '').toString().trim();
    if (v) {
      cacheCapacitorAppVersion(v);
      return v;
    }
  } catch (_) {
    // ignore
  }
  return null;
};

if (typeof window !== 'undefined' && !window.__MX_FETCH_PATCHED__) {
  try {
    window.__MX_FETCH_PATCHED__ = true;
    const originalFetch = window.fetch?.bind(window);
    if (typeof originalFetch === 'function') {
      window.fetch = async (input, init) => {
        try {
          if (!isNativeCapacitor) return originalFetch(input, init);

          const v = await ensureCapacitorAppVersionCached();
          if (!v) return originalFetch(input, init);

          const url = (typeof input === 'string')
            ? input
            : (input && typeof input.url === 'string' ? input.url : '');

          const isApi = url.startsWith('/api/') || url.includes('/api/');
          if (!isApi) return originalFetch(input, init);

          if (input && typeof input === 'object' && typeof input.headers === 'object') {
            const h = new Headers(input.headers);
            if (!h.has('x-app-version')) h.set('x-app-version', v);
            const req = new Request(input, { headers: h });
            return originalFetch(req, init);
          }

          const headers = new Headers((init && init.headers) || undefined);
          if (!headers.has('x-app-version')) headers.set('x-app-version', v);
          return originalFetch(input, { ...(init || {}), headers });
        } catch (_) {
          return originalFetch(input, init);
        }
      };
    }
  } catch (_) {
    // ignore
  }
}

const isInvalidOrExpiredTokenMessage = (message) => {
  const msg = (message || '').toString().toLowerCase();
  if (!msg) return false;

  return (
    msg.includes('invalid token') ||
    msg.includes('token expired') ||
    msg.includes('no token provided') ||
    msg.includes('user not found') ||
    msg.includes('token inválido') ||
    msg.includes('token expirado')
  );
};

const isPublicRoute = (pathname) => {
  if (!pathname) return false;
  
  // Normalize path if it comes from window.location.hash (HashRouter)
  let normalizedPath = pathname;
  if (pathname.startsWith('#/')) {
    normalizedPath = pathname.slice(1).split('?')[0];
  } else if (pathname.includes('#/')) {
    normalizedPath = pathname.split('#/')[1].split('?')[0];
    if (!normalizedPath.startsWith('/')) normalizedPath = '/' + normalizedPath;
  } else {
    normalizedPath = pathname.split('?')[0];
  }

  // Si estamos en un subdominio de catálogo o tienda (ej: cai-store.mundoxyz.com) y no es /admin
  if (typeof window !== 'undefined') {
    const hostname = (window.location.hostname || '').toLowerCase();
    const isSubdomain = (hostname.endsWith('.mundoxyz.com') || hostname.endsWith('.localhost')) &&
      !['www', 'api', 'admin', 'app', 'cdn', 'media', 's3', 'r2', 'assets', 'storage'].includes(hostname.split('.')[0]);
    if (isSubdomain && !normalizedPath.startsWith('/admin')) {
      return true;
    }
  }

  const publicRoutes = [
    /^\/?$/, // Home / Landing
    /^\/c\/[^/]+(?:\/.*)?$/i, // Catálogos multi-tienda (/c/:storeSlug, /c/:storeSlug/pedido/..., etc.)
    /^\/catalogo(?:\/.*)?$/i, // Catálogo principal y subrutas
    /^\/(belenshop|hotel-aguamarina|aguamarina|maho-albi|mahoalbi|gamingspot|roel)(?:\/.*)?$/i, // Slugs de catálogo dedicados
    /^\/store\/[^/]+(?:\/(invoice|qr)(?:\/[^/]+)?)?\/?$/i, // Storefront, invoice, qr
    /^\/transport\/pay\/[^/]+\/?$/i,
    /^\/market(?:\/.*)?$/i,
    /^\/gift-link\/[^/]+\/?$/i,
    /^\/pedido\/[^/]+(?:\/.*)?$/i,
    /^\/crear-(catalogo|tienda)\/?$/i,
    /^\/(tito|changelog|desaparecidos|ayuda-mutua|transparencia|cronologia)\/?$/i,
    /^\/(terminos|terms|privacidad|privacy|dmca)\/?$/i,
    /^\/(login|register|registerxyz23|reset-password)\/?$/i,
  ];
  return publicRoutes.some((re) => re.test(normalizedPath));
};

let notifySessionExpired = null;

// Add request interceptor
axios.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const rateLimitUntil = typeof window !== 'undefined' ? window.__RATE_LIMIT_UNTIL__ : null;
      if (rateLimitUntil && Date.now() < rateLimitUntil) {
        config.headers['x-client-rate-limited'] = '1';
      }
    } catch (_) {
      // ignore
    }

    const method = (config.method || 'get').toLowerCase();
    if (method === 'get') {
      config.headers['Cache-Control'] = config.headers['Cache-Control'] || 'no-cache';
      config.headers['Pragma'] = config.headers['Pragma'] || 'no-cache';
    }

    if (isNativeCapacitor) {
      const v = await ensureCapacitorAppVersionCached();
      if (v) {
        config.headers['x-app-version'] = config.headers['x-app-version'] || v;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Mandatory update required by backend
    if (error.response?.status === 426 && isNativeCapacitor) {
      const data = error.response?.data || {};
      const downloadUrl = data.downloadUrl || '/app/download/android';

      try {
        if (typeof notifyMandatoryUpdate === 'function') {
          notifyMandatoryUpdate({
            isOpen: true,
            mandatory: true,
            currentVersion: data.currentVersion || null,
            latestVersion: data.latestVersion || null,
            minVersion: data.minVersion || null,
            downloadUrl,
          });
        }
      } catch (_) {
        // ignore
      }

      try {
        const browserPlugin =
          typeof window !== 'undefined'
            ? window.Capacitor?.Plugins?.Browser
            : null;
        if (browserPlugin?.open) {
          await browserPlugin.open({ url: downloadUrl });
        } else if (typeof window !== 'undefined') {
          window.location.href = downloadUrl;
        }
      } catch (_) {
        if (typeof window !== 'undefined') {
          window.location.href = downloadUrl;
        }
      }

      try {
        toast.error('Actualización obligatoria. Debes actualizar para continuar.');
      } catch (_) {
        // ignore
      }
      return Promise.reject(error);
    }

    // Manejar error 429 (rate limit) - NO hacer logout
    if (error.response?.status === 429) {
      const cfg = error.config || {};
      const method = (cfg.method || 'get').toLowerCase();

      const rawRetryAfter = error.response.headers?.['retry-after'];
      const parsedRetryAfter = Number(rawRetryAfter);
      const retryAfterSeconds = Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0
        ? parsedRetryAfter
        : 5;

      const now = Date.now();
      const minCooldownUntil = now + retryAfterSeconds * 1000;

      try {
        const existing = typeof window !== 'undefined' ? window.__RATE_LIMIT_UNTIL__ : null;
        const existingTs = Number(existing);
        const nextUntil = Number.isFinite(existingTs)
          ? Math.max(existingTs, minCooldownUntil)
          : minCooldownUntil;
        if (typeof window !== 'undefined') {
          window.__RATE_LIMIT_UNTIL__ = nextUntil;
        }
      } catch (_) {
        // ignore
      }

      const currentRetryCount = Number(cfg.__rateLimitRetryCount || 0);
      console.warn(`Rate limit alcanzado (429). retry-after=${retryAfterSeconds}s, method=${method}, retry=${currentRetryCount}`);

      // Only retry GET requests and limit retries to 2 to avoid blocking the UI
      const maxRetries = 2;
      const isRetryable = method === 'get' && currentRetryCount < maxRetries;

      if (isRetryable) {
        // Linear backoff instead of exponential to avoid long hangs
        const waitSeconds = Math.max(retryAfterSeconds, currentRetryCount + 1);
        
        const nextConfig = {
          ...cfg,
          __rateLimitRetryCount: currentRetryCount + 1,
        };

        await new Promise((resolve) => setTimeout(resolve, waitSeconds * 1000));
        return axios(nextConfig);
      }

      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Excluir endpoints de contraseña del logout automático
      const url = error.config?.url || '';
      const isPasswordEndpoint = url.includes('/check-password') ||
        url.includes('/change-password');

      const backendMessage = error.response?.data?.error || '';
      const isTokenInvalidOrExpired = isInvalidOrExpiredTokenMessage(backendMessage);

      // Solo hacer logout si NO es un endpoint de contraseña
      if (!isPasswordEndpoint && isTokenInvalidOrExpired) {
        // Token expired or invalid
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Notify React state to clear user (prevents infinite re-renders or stale state)
        if (typeof notifySessionExpired === 'function') {
          notifySessionExpired();
        }

        // Only redirect if not already on a public route and not suppressed
        const currentPath = window.location.hash || window.location.pathname;
        if (!isPublicRoute(currentPath) && !error.config?.__suppress401Redirect) {
          window.location.href = '/login';
        }
      }
    }

    if (error.response?.status === 403) {
      const cfg = error.config || {};
      if (cfg.__suppress403Toast) {
        return Promise.reject(error);
      }
      const message = error.response?.data?.error || '';
      const level = error.response?.data?.store_level;

      if (message.includes('Acceso a billetera y tokens no permitido para tu nivel de tienda')) {
        const extra =
          typeof level === 'number'
            ? ` (nivel actual: ${level}. Se requiere nivel 2 o 3)`
            : '';
        toast.error(
          `Tu tienda no tiene acceso a billetera y tokens.${extra}`
        );
      } else if (message.includes('Acceso a juegos no permitido para tu nivel de tienda')) {
        const extra =
          typeof level === 'number'
            ? ` (nivel actual: ${level}. Se requiere nivel 3)`
            : '';
        toast.error(
          `Tu tienda no tiene acceso a juegos.${extra}`
        );
      } else {
        toast.error(getBackendErrorMessage(error, 'Acceso no permitido'));
      }
    }
    return Promise.reject(error);
  }
);

const MX_ACCOUNTS_KEY = 'mx_accounts';
const MAX_SAVED_ACCOUNTS = 5;

const getSavedAccounts = () => {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(MX_ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const saveAccount = (userData, token) => {
  try {
    if (!userData?.id || !token || typeof window === 'undefined') return;
    const accounts = getSavedAccounts();
    const snapshot = {
      id: userData.id,
      username: userData.username || '',
      display_name: userData.display_name || userData.username || '',
      avatar_url: userData.avatar_url || null,
      token,
      addedAt: new Date().toISOString()
    };
    const existingIndex = accounts.findIndex((a) => a.id === snapshot.id);
    if (existingIndex >= 0) {
      accounts[existingIndex] = snapshot;
    } else {
      accounts.push(snapshot);
    }
    if (accounts.length > MAX_SAVED_ACCOUNTS) {
      accounts.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt));
      accounts.splice(0, accounts.length - MAX_SAVED_ACCOUNTS);
    }
    window.localStorage.setItem(MX_ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch (_) {
    // ignore storage errors
  }
};

const removeSavedAccount = (userId) => {
  try {
    if (!userId || typeof window === 'undefined') return;
    const accounts = getSavedAccounts().filter((a) => a.id !== userId);
    window.localStorage.setItem(MX_ACCOUNTS_KEY, JSON.stringify(accounts));
  } catch (_) {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionValidationRanRef = useRef(false);
  const updateCheckRanRef = useRef(false);

  const [appUpdateState, setAppUpdateState] = useState({
    isOpen: false,
    mandatory: false,
    currentVersion: null,
    latestVersion: null,
    minVersion: null,
    downloadUrl: null,
  });

  const [savedAccounts, setSavedAccounts] = useState(() => getSavedAccounts());

  useEffect(() => {
    notifyMandatoryUpdate = (next) => {
      setAppUpdateState((prev) => ({
        ...prev,
        ...(next || {}),
      }));
    };

    notifySessionExpired = () => {
      setUser(null);
    };

    return () => {
      notifyMandatoryUpdate = null;
      notifySessionExpired = null;
    };
  }, []);

  // Listener para Deep Links (Google Auth en Capacitor)
  useEffect(() => {
    if (!isNativeCapacitor) return;

    let listenerObj = null;

    const setupListener = async () => {
      try {
        const capApp = await import('@capacitor/app');
        listenerObj = await capApp.App.addListener('appUrlOpen', async (data) => {
          console.log('App opened with URL:', data.url);
          if (data.url.includes('/auth/google/callback')) {
            try {
              const url = new URL(data.url);
              const token = url.searchParams.get('token');
              const userParam = url.searchParams.get('user');
              const nextParam = url.searchParams.get('next') || '/lobby';

              if (token && userParam) {
                const normalizedBase64 = userParam.replace(/-/g, '+').replace(/_/g, '/');
                const paddedBase64 = normalizedBase64.padEnd(Math.ceil(normalizedBase64.length / 4) * 4, '=');
                const decodedUser = JSON.parse(decodeURIComponent(escape(window.atob(paddedBase64))));
                
                persistSession(token, decodedUser);
                toast.success('¡Bienvenido a MUNDOXYZ!');
                
                // Redirigir usando window.location.href para forzar recarga del enrutador si es necesario
                window.location.href = nextParam;
              }
            } catch (err) {
              console.error('Error parsing Google auth deep link:', err);
              toast.error('Error al procesar el inicio de sesión con Google');
            }
          }
        });
      } catch (err) {
        console.warn('Could not setup Capacitor AppUrlOpen listener:', err);
      }
    };

    setupListener();

    return () => {
      if (listenerObj && listenerObj.remove) {
        listenerObj.remove();
      }
    };
  }, []);


  // Helper para normalizar roles siempre como array
  const normalizeUserData = (userData) => {
    return {
      ...userData,
      my_referrer_code: userData?.my_referrer_code || userData?.username || null,
      roles: Array.isArray(userData?.roles)
        ? userData.roles
          .map(r => (typeof r === 'string' ? r : r?.name))
          .filter(Boolean)
          .map(r => r.toLowerCase())
        : (userData?.roles
          ? [
            (typeof userData.roles === 'string' ? userData.roles : userData.roles?.name)
          ]
            .filter(Boolean)
            .map(r => r.toLowerCase())
          : ['user']),
      // Normalizar security_answer: backend puede enviar has_security_answer o security_answer
      security_answer: userData?.security_answer !== undefined
        ? userData.security_answer
        : (userData?.has_security_answer || false),
      security_question: userData?.security_question || null,
      wallet_address: userData?.wallet_address || null,
      must_change_password: !!userData?.must_change_password,
      home_store_id: userData?.home_store_id || null,
      home_store_slug: userData?.home_store_slug || null,
      home_store_name: userData?.home_store_name || null,
      home_store: userData?.home_store_id
        ? { id: userData.home_store_id, slug: userData.home_store_slug, name: userData.home_store_name || null }
        : userData?.home_store_slug
        ? { slug: userData.home_store_slug, name: userData.home_store_name || null }
        : null
    };
  };

  useEffect(() => {
    if (sessionValidationRanRef.current) return;
    sessionValidationRanRef.current = true;
    // Check for existing session
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Validate token with backend
          const response = await axios.get('/api/roles/me', { params: { _ts: Date.now() } });
          const userData = JSON.parse(storedUser);
          
          // Validar que el usuario no esté corrupto
          if (!userData || !userData.id || userData.id === 'undefined' || userData.id === 'null') {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            return;
          }

          // Asegurar que roles sea un array y manejar ambos formatos
          const rolesArray = response.data.roles || [];
          const normalizedRoles = rolesArray
            .map(r => (typeof r === 'string' ? r : r?.name))
            .filter(Boolean)
            .map(r => r.toLowerCase());

          const walletAddress = response.data.wallet_address || userData.wallet_address || null;

          let freshUser = {
            ...userData,
            wallet_address: walletAddress,
            roles: normalizedRoles
          };

          try {
            const profileResponse = await axios.get(`/api/profile/${userData.id}`, { params: { _ts: Date.now() } });
            const profileData = profileResponse.data;
            if (profileData) {
              freshUser = {
                id: profileData.id,
                username: profileData.username,
                display_name: profileData.display_name,
                nickname: profileData.nickname,
                bio: profileData.bio,
                email: profileData.email,
                phone: profileData.phone || null,
                is_phone_verified: !!profileData.is_phone_verified,
                tg_id: profileData.tg_id,
                avatar_url: profileData.avatar_url,
                locale: profileData.locale,
                is_verified: profileData.is_verified,
                created_at: profileData.created_at,
                last_seen_at: profileData.last_seen_at,
                roles: normalizedRoles,
                wallet_id: profileData.wallet_id,
                wallet_address: profileData.wallet_address || walletAddress,
                security_answer: profileData.security_answer || false,
                security_question: profileData.security_question || null,
                coins_balance: profileData.stats?.coins_balance || 0,
                fires_balance: profileData.stats?.fires_balance || 0,
                total_coins_earned: profileData.stats?.total_coins_earned || 0,
                total_fires_earned: profileData.stats?.total_fires_earned || 0,
                total_coins_spent: profileData.total_coins_spent || 0,
                total_fires_spent: profileData.total_fires_spent || 0,
                experience: profileData.experience || 0,
                total_games_played: profileData.total_games_played || 0,
                total_games_won: profileData.total_games_won || 0,
                home_store_slug: profileData.home_store_slug ?? userData.home_store_slug ?? null,
                home_store_name: profileData.home_store_name ?? userData.home_store_name ?? null,
                home_store: profileData.home_store_slug
                  ? { slug: profileData.home_store_slug, name: profileData.home_store_name || null }
                  : (userData.home_store || null)
              };
            }
          } catch (profileError) {
            console.error('Error fetching full profile during checkAuth, continuing with cached/roles data:', profileError);
          }

          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        } catch (error) {
          console.error('Session validation failed:', error);
          // Si el JSON en localStorage está corrupto o hay un SyntaxError, limpiar sesión inmediatamente
          if (error instanceof SyntaxError || error.name === 'SyntaxError') {
            console.warn('Datos de sesión corruptos en localStorage, limpiando...');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          } else if (error.response?.status === 401) {
            const backendMessage = error.response?.data?.error || '';
            if (isInvalidOrExpiredTokenMessage(backendMessage)) {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
            }
          } else if (error.response?.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          } else if (error.response?.status === 429) {
            console.warn('Rate limit alcanzado, sesión válida pero temporalmente bloqueada');
            // Mantener token y user, solo loggear el warning
          }
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (updateCheckRanRef.current) return;
    updateCheckRanRef.current = true;
    if (!isNativeCapacitor) return;

    const run = async () => {
      try {
        const currentVersion = await ensureCapacitorAppVersionCached();
        const response = await axios.get('/api/app/android/latest', { params: { _ts: Date.now() } });
        const data = response?.data || {};
        const minVersion = data.minVersion || null;

        if (!currentVersion || !minVersion) return;
        const cmp = compareVersions(currentVersion, minVersion);
        if (cmp == null || cmp !== -1) return;

        const downloadUrl = data.downloadUrl || '/app/download/android';

        try {
          if (typeof notifyMandatoryUpdate === 'function') {
            notifyMandatoryUpdate({
              isOpen: true,
              mandatory: true,
              currentVersion,
              latestVersion: data.latestVersion || null,
              minVersion,
              downloadUrl,
            });
          }
        } catch (_) {
          // ignore
        }

        try {
          const browserPlugin =
            typeof window !== 'undefined'
              ? window.Capacitor?.Plugins?.Browser
              : null;
          if (browserPlugin?.open) {
            await browserPlugin.open({ url: downloadUrl });
          } else if (typeof window !== 'undefined') {
            window.location.href = downloadUrl;
          }
        } catch (_) {
          if (typeof window !== 'undefined') {
            window.location.href = downloadUrl;
          }
        }

        try {
          toast.error('Actualización obligatoria. Debes actualizar para continuar.');
        } catch (_) {
          // ignore
        }
      } catch (err) {
        // Log para debugging sin romper la app
        if (err?.response?.status === 426) {
          // Ya manejado por interceptor de axios (no es un error real)
          return;
        }
        console.warn('[CapacitorVersionCheck] Error verificando version:', err?.message || err);
      }
    };

    run();
  }, []);

  const loginWithTelegram = async () => {
    try {
      setLoading(true);

      // Get Telegram WebApp data
      const tg = window.Telegram?.WebApp;
      if (!tg?.initData) {
        throw new Error('Telegram WebApp no disponible');
      }

      // Incluir código de referido si existe en localStorage
      let refPayload = null;
      try {
        const raw = window.localStorage.getItem('mx_referral_code');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.code && Date.now() - (parsed.ts || 0) <= 7 * 24 * 60 * 60 * 1000) {
            refPayload = parsed.code;
          }
        }
      } catch (_) {}

      const response = await axios.post('/api/auth/login-telegram', {
        initData: tg.initData,
        ref: refPayload
      });

      const { token, user: userData } = response.data;

      // Normalizar datos del usuario
      const normalizedUser = normalizeUserData(userData);

      // Store token and user
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      setUser(normalizedUser);
      saveAccount(normalizedUser, token);
      setSavedAccounts(getSavedAccounts());
      toast.success('¡Bienvenido a MUNDOXYZ!');

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      toast.error(getBackendErrorMessage(error, 'Error al iniciar sesión'));
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithCredentials = async (username, password) => {
    try {
      setLoading(true);

      const response = await axios.post('/api/auth/login-email', {
        email: username,
        password
      });

      const { token, user: userData } = response.data;

      // Normalizar datos del usuario
      const normalizedUser = normalizeUserData(userData);

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      setUser(normalizedUser);
      saveAccount(normalizedUser, token);
      setSavedAccounts(getSavedAccounts());
      toast.success('¡Bienvenido a MUNDOXYZ!');

      return { success: true, user: normalizedUser };
    } catch (error) {
      console.error('Login error:', error);

      // Manejar caso de usuario sin contraseña configurada (reseteado por admin)
      if (error.response?.data?.code === 'NEEDS_PASSWORD_SETUP' || error.response?.data?.needs_password_setup) {
        return { 
          success: false, 
          needs_password_setup: true,
          user_id: error.response.data.user_id,
          username: error.response.data.username,
          has_security_answer: error.response.data.has_security_answer
        };
      }
      
      toast.error(getBackendErrorMessage(error, 'Error al iniciar sesión'));
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const persistSession = (token, userData) => {
    const normalizedUser = normalizeUserData(userData);
    if (token) {
      localStorage.setItem('token', token);
    }
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    setUser(normalizedUser);
    saveAccount(normalizedUser, token);
    setSavedAccounts(getSavedAccounts());
    return normalizedUser;
  };

  const setToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      setLoading(true);

      const response = await axios.post('/api/auth/login-google', {
        credential
      });

      const { token, user: userData } = response.data;
      const normalizedUser = persistSession(token, userData);
      toast.success('¡Bienvenido a MUNDOXYZ!');

      return { success: true, user: normalizedUser };
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(getBackendErrorMessage(error, 'Error al iniciar sesión con Google'));
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (formData) => {
    try {
      setLoading(true);

      const titoToken = typeof window !== 'undefined' ? localStorage.getItem('tito_token') : null;
      const referralCode = (formData?.ref || '').toString().trim() || null;

      const response = await axios.post('/api/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        titoToken: titoToken || undefined,
        ref: referralCode,
        termsAccepted: !!formData.termsAccepted,
        isAdult: !!formData.isAdult
      });
      const { token, user: userData } = response.data;

      // Normalizar datos del usuario
      const normalizedUser = normalizeUserData(userData);

      // Guardar sesión igual que en login
      if (token) {
        localStorage.setItem('token', token);
      }
      localStorage.setItem('user', JSON.stringify(normalizedUser));

      // Una vez registrado, ya no necesitamos el token local de Tito en este dispositivo
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('tito_token');
        }
      } catch (_) { }

      setUser(normalizedUser);
      saveAccount(normalizedUser, token);
      setSavedAccounts(getSavedAccounts());

      toast.success(response.data.message || '¡Registro exitoso!');

      return { success: true, user: normalizedUser };
    } catch (error) {
      console.error('Registration error:', error);
      const status = error?.response?.status;
      const rawBackendMessage = (error?.response?.data?.error || '').toString().trim();
      const errorMessage = getBackendErrorMessage(error, 'Error al registrar usuario');

      const fieldErrors = {};
      const msg = (rawBackendMessage || errorMessage).toLowerCase();

      if (status === 400) {
        if (msg.includes('formato de email inválido') || msg.includes('email inválido')) {
          fieldErrors.email = 'Email inválido';
        }
        if (msg.includes('contraseña debe tener al menos 6')) {
          fieldErrors.password = 'Mínimo 6 caracteres';
        }
        if (msg.includes('usuario debe tener entre 3 y 20')) {
          fieldErrors.username = 'El usuario debe tener entre 3 y 20 caracteres';
        }
        if (msg.includes('solo puede contener letras')) {
          fieldErrors.username = 'Solo letras, números y guiones bajos';
        }
        if (msg.includes('todos los campos son requeridos')) {
          fieldErrors.form = 'Por favor completa todos los campos requeridos';
        }
      }

      if (status === 409) {
        if (msg.includes('correo') && msg.includes('ya está registrado')) {
          fieldErrors.email = 'Este correo ya está registrado';
        }
        if (msg.includes('usuario') && msg.includes('ya está registrado')) {
          fieldErrors.username = 'Este usuario ya está registrado';
        }
      }

      toast.error(errorMessage);
      return { success: false, error: errorMessage, fieldErrors };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      window.location.href = '/login';
    }
  };

  const updateUser = (userData) => {
    const normalizedUser = normalizeUserData(userData);
    setUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const refreshUser = async (explicitUserId = null) => {
    try {
      const targetUserId = explicitUserId || user?.id || (() => {
        try {
          return JSON.parse(localStorage.getItem('user') || '{}')?.id;
        } catch {
          return null;
        }
      })();

      if (!targetUserId) return null;

      const response = await axios.get(`/api/profile/${targetUserId}`, {
        params: { _ts: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const profileData = response.data;
      if (!profileData) return null;

      // Construir usuario actualizado con TODOS los campos nuevos
      const updatedUser = {
        id: profileData.id,
        username: profileData.username,
        display_name: profileData.display_name,
        nickname: profileData.nickname,
        bio: profileData.bio,
        email: profileData.email,
        phone: profileData.phone || null,
        is_phone_verified: !!profileData.is_phone_verified,
        tg_id: profileData.tg_id,
        avatar_url: profileData.avatar_url,
        locale: profileData.locale,
        is_verified: profileData.is_verified,
        created_at: profileData.created_at,
        updated_at: profileData.updated_at || user?.updated_at || null,
        last_seen_at: profileData.last_seen_at,
        roles: Array.isArray(profileData.roles) ? profileData.roles :
          Array.isArray(user?.roles) ? user.roles : [],
        wallet_id: profileData.wallet_id,
        wallet_address: profileData.wallet_address,
        // Seguridad
        security_answer: profileData.security_answer || false,
        security_question: profileData.security_question || null,
        // Balances del stats
        coins_balance: parseFloat(profileData.stats?.coins_balance ?? profileData.coins_balance ?? user?.coins_balance ?? 0),
        fires_balance: parseFloat(profileData.stats?.fires_balance ?? profileData.fires_balance ?? user?.fires_balance ?? 0),
        total_coins_earned: profileData.stats?.total_coins_earned || 0,
        total_fires_earned: profileData.stats?.total_fires_earned || 0,
        total_coins_spent: profileData.total_coins_spent || 0,
        total_fires_spent: profileData.total_fires_spent || 0,
        // Experiencia
        experience: profileData.experience || 0,
        total_games_played: profileData.total_games_played || 0,
        total_games_won: profileData.total_games_won || 0,
        // Tienda principal (home store) para botón verde / POS / panel de tienda
        home_store_slug: profileData.home_store_slug ?? user?.home_store_slug ?? null,
        home_store_name: profileData.home_store_name ?? user?.home_store_name ?? null,
        home_store: (profileData.home_store_slug
          ? { slug: profileData.home_store_slug, name: profileData.home_store_name || null }
          : (user?.home_store || null))
      };

      updateUser(updatedUser);

      // Sincronizar cuenta guardada
      const token = localStorage.getItem('token');
      if (token) {
        saveAccount(updatedUser, token);
      }

      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const hasRole = (role) => {
    // Debug logging para APK - versión simplificada para Android
    console.log('🔍 USER ID:', user?.id);
    console.log('🔍 USERNAME:', user?.username);
    console.log('🔍 ROLES:', JSON.stringify(user?.roles));
    console.log('🔍 CHECKING ROLE:', role);
    console.log('🔍 HAS ROLE:', user?.roles?.includes(role));
    console.log('---');
    return user?.roles?.includes(role) || false;
  };

  const isAdmin = () => {
    // Fallback para el usuario principal Tote por Username o TG_ID
    const isMasterAdmin = 
      user?.username?.toLowerCase() === 'tote' || 
      user?.username?.toLowerCase() === 'wilcnct' || // Username real encontrado
      user?.tg_id?.toString() === '1417856820';
      
    return hasRole('admin') || hasRole('tote') || isMasterAdmin;
  };

  const isTote = () => {
    // Fallback para el usuario principal Tote por Username o TG_ID
    const isMasterTote = 
      user?.username?.toLowerCase() === 'tote' || 
      user?.username?.toLowerCase() === 'wilcnct' || 
      user?.tg_id?.toString() === '1417856820';

    return hasRole('tote') || isMasterTote;
  };

  const switchAccount = (account) => {
    try {
      if (!account?.id || !account.token || typeof window === 'undefined') return;
      localStorage.setItem('token', account.token);
      const minimalUser = {
        id: account.id,
        username: account.username || '',
        display_name: account.display_name || account.username || '',
        avatar_url: account.avatar_url || null
      };
      localStorage.setItem('user', JSON.stringify(minimalUser));
      window.location.reload();
    } catch (_) {
      // ignore
    }
  };

  const refreshSavedAccounts = () => {
    setSavedAccounts(getSavedAccounts());
  };

  const handleRemoveSavedAccount = (userId) => {
    removeSavedAccount(userId);
    setSavedAccounts(getSavedAccounts());
  };

  const value = React.useMemo(() => ({
    user,
    loading,
    loginWithTelegram,
    loginWithCredentials,
    loginWithGoogle,
    persistSession,
    setToken,
    setUser,
    register,
    logout,
    updateUser,
    refreshUser,
    hasRole,
    isAdmin,
    isTote,
    savedAccounts,
    switchAccount,
    removeSavedAccount: handleRemoveSavedAccount,
    refreshSavedAccounts
  }), [user, loading, savedAccounts]);

  return (
    <AuthContext.Provider value={value}>
      <AppUpdateRequiredModal
        isOpen={!!appUpdateState.isOpen}
        mandatory={!!appUpdateState.mandatory}
        currentVersion={appUpdateState.currentVersion}
        latestVersion={appUpdateState.latestVersion}
        minVersion={appUpdateState.minVersion}
        downloadUrl={appUpdateState.downloadUrl}
        onUpdate={async () => {
          const url = appUpdateState.downloadUrl || '/app/download/android';
          try {
            const browserPlugin =
              typeof window !== 'undefined'
                ? window.Capacitor?.Plugins?.Browser
                : null;
            if (browserPlugin?.open) {
              await browserPlugin.open({ url });
            } else if (typeof window !== 'undefined') {
              window.location.href = url;
            }
          } catch (_) {
            if (typeof window !== 'undefined') {
              window.location.href = url;
            }
          }
        }}
      />
      {children}
    </AuthContext.Provider>
  );
};
